import { useState, useEffect } from "react";
import { toast } from "sonner";
import { base44 } from "@/api/base44Client";
import { useCloudDrafts } from "@/hooks/useCloudDrafts";
import CloudDraftsPanel from "@/components/CloudDraftsPanel";
import { useAuth } from "@/lib/AuthContext";
import { 
  FileText, 
  Upload, 
  Trash2, 
  CheckCircle2, 
  Clock, 
  Calendar, 
  Plus, 
  RefreshCw,
  FolderOpen,
  FileCheck
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import ActionstepSavePanel from "@/components/ActionstepSavePanel";
import { actionstepFolderFor } from "@/lib/actionstepFolders";
import { createPortalPdf, portalDocumentName } from "@/lib/portalPdf";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COURTS = [
  "Magistrates' Court",
  "Children's Court",
  "County Court",
  "Supreme Court",
  "Court of Appeal",
  "High Court of Australia"
];

const JUSTICE_PORTAL_URL = "https://justice.service-now.com/acb?id=acb_application_form&sys_id=76becca4db33c300b3c5326f7c961997";

const BRAND_DETAILS = {
  rga: {
    orgName: "Robyn Greensill & Associates",
    appPrefix: "RGA",
    defaultEmail: "rga@completelawsupport.com.au",
    textColor: "text-teal-700",
    textDarkColor: "text-teal-700 dark:text-teal-400",
    textMutedColor: "text-teal-800 dark:text-teal-400 bg-teal-50/50",
    textHeaderColor: "text-teal-100",
    iconColor: "text-teal-700",
    iconSmallColor: "text-teal-600",
    borderColor: "border-teal-100",
    borderActiveColor: "border-teal-600",
    bgHeader: "bg-gradient-to-r from-teal-800 to-teal-700",
    bgButton: "bg-teal-700 hover:bg-teal-800 focus:ring-teal-500",
    bgDragActive: "border-teal-500 bg-teal-50/50",
    borderDragInactive: "border-slate-200 hover:border-teal-400",
    bgReviewActive: "bg-teal-50/30 dark:bg-teal-950/20 border-l-4 border-teal-600",
    textFileName: "text-teal-700 break-all",
  },
  vals: {
    orgName: "Victorian Aboriginal Legal Service",
    appPrefix: "VALS",
    defaultEmail: "reception@vals.org.au",
    textColor: "text-rose-700",
    textDarkColor: "text-rose-700 dark:text-rose-400",
    textMutedColor: "text-rose-800 dark:text-rose-400 bg-rose-50/50",
    textHeaderColor: "text-rose-100",
    iconColor: "text-rose-700",
    iconSmallColor: "text-rose-600",
    borderColor: "border-rose-100",
    borderActiveColor: "border-rose-600",
    bgHeader: "bg-gradient-to-r from-rose-800 to-rose-700",
    bgButton: "bg-rose-700 hover:bg-rose-800 focus:ring-rose-500",
    bgDragActive: "border-rose-500 bg-rose-50/50",
    borderDragInactive: "border-slate-200 hover:border-rose-400",
    bgReviewActive: "bg-rose-50/30 dark:bg-rose-950/20 border-l-4 border-rose-600",
    textFileName: "text-rose-700 break-all",
  },
  // EAS is the firm's name in full: it does not stand for anything.
  eas: {
    orgName: "EAS",
    appPrefix: "EAS",
    defaultEmail: "admin@completelawsupport.com",
    textColor: "text-amber-700",
    textDarkColor: "text-amber-700 dark:text-amber-400",
    textMutedColor: "text-amber-800 dark:text-amber-400 bg-amber-50/50",
    textHeaderColor: "text-amber-100",
    iconColor: "text-amber-700",
    iconSmallColor: "text-amber-600",
    borderColor: "border-amber-100",
    borderActiveColor: "border-amber-600",
    bgHeader: "bg-gradient-to-r from-amber-800 to-amber-700",
    bgButton: "bg-amber-700 hover:bg-amber-800 focus:ring-amber-500",
    bgDragActive: "border-amber-500 bg-amber-50/50",
    borderDragInactive: "border-slate-200 hover:border-amber-400",
    bgReviewActive: "bg-amber-50/30 dark:bg-amber-950/20 border-l-4 border-amber-600",
    textFileName: "text-amber-700 break-all",
  },
  lacw: {
    orgName: "Law & Advocacy Centre for Women",
    appPrefix: "LACW",
    defaultEmail: "info@lacw.org.au",
    textColor: "text-purple-700",
    textDarkColor: "text-purple-700 dark:text-purple-400",
    textMutedColor: "text-purple-800 dark:text-purple-400 bg-purple-50/50",
    textHeaderColor: "text-purple-100",
    iconColor: "text-purple-700",
    iconSmallColor: "text-purple-600",
    borderColor: "border-purple-100",
    borderActiveColor: "border-purple-600",
    bgHeader: "bg-gradient-to-r from-purple-800 to-purple-700",
    bgButton: "bg-purple-700 hover:bg-purple-800 focus:ring-purple-500",
    bgDragActive: "border-purple-500 bg-purple-50/50",
    borderDragInactive: "border-slate-200 hover:border-purple-400",
    bgReviewActive: "bg-purple-50/30 dark:bg-purple-950/20 border-l-4 border-purple-600",
    textFileName: "text-purple-700 break-all",
  },
  admin: {
    orgName: "Complete Law Support",
    appPrefix: "CLS",
    defaultEmail: "admin@completelawsupport.com",
    textColor: "text-blue-700",
    textDarkColor: "text-blue-700 dark:text-blue-400",
    textMutedColor: "text-blue-800 dark:text-blue-400 bg-blue-50/50",
    textHeaderColor: "text-blue-100",
    iconColor: "text-blue-700",
    iconSmallColor: "text-blue-600",
    borderColor: "border-blue-100",
    borderActiveColor: "border-blue-600",
    bgHeader: "bg-gradient-to-r from-blue-800 to-blue-700",
    bgButton: "bg-blue-700 hover:bg-blue-800 focus:ring-blue-500",
    bgDragActive: "border-blue-500 bg-blue-50/50",
    borderDragInactive: "border-slate-200 hover:border-blue-400",
    bgReviewActive: "bg-blue-50/30 dark:bg-blue-950/20 border-l-4 border-blue-600",
    textFileName: "text-blue-700 break-all",
  }
};

export default function AppealCostCertificate({ isPortalMode = false, brandType = "rga", isAdminMode = false }) {
  const { user } = useAuth();
  const { drafts, fetchDrafts, saveDraft, deleteDraft, isLoading } = useCloudDrafts();
  const brandObj = BRAND_DETAILS[brandType] || BRAND_DETAILS.rga;

  // Selected draft ID for active draft tracking
  const [selectedDraftId, setSelectedDraftId] = useState("");

  // Matters list for prefill association
  const [matters, setMatters] = useState([]);
  const [selectedMatterId, setSelectedMatterId] = useState("");

  // Form states for Lodging New Certificate (Solicitor mode)
  const [caseName, setCaseName] = useState("");
  const [caseNumber, setCaseNumber] = useState("");
  const [court, setCourt] = useState("");
  const [certificateDate, setCertificateDate] = useState(new Date().toISOString().split("T")[0]);
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  
  // File state
  const [attachedFile, setAttachedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Selected review lodgment ID (Admin mode)
  const [selectedReviewId, setSelectedReviewId] = useState("");
  const [inputApplicationId, setInputApplicationId] = useState("");
  const [uploadingOutcome, setUploadingOutcome] = useState(false);

  const [supportingFiles, setSupportingFiles] = useState([]);
  const [uploadingSupporting, setUploadingSupporting] = useState(false);
  const [uploadingCertificate, setUploadingCertificate] = useState(false);
  const [inputCaseNumber, setInputCaseNumber] = useState("");
  const [inputAmount, setInputAmount] = useState("");

  // Load drafts and matters on mount
  useEffect(() => {
    fetchDrafts("appeal_cost_certificate", brandType);
    
    base44.matters.list()
      .then(setMatters)
      .catch(err => {
        console.error("Failed to load matters:", err);
      });
  }, [fetchDrafts, brandType]);

  // Separate pending, lodged, finalised, and draft lodgments
  const pendingLodgments = drafts.filter(d => d.form_data?.status === "pending");
  const lodgedLodgments = drafts.filter(d => d.form_data?.status === "lodged");
  const finalisedLodgments = drafts.filter(d => d.form_data?.status === "finalised");
  const savedDrafts = drafts.filter(d => d.form_data?.status === "draft");

  // Auto-select first available lodgment in Admin mode if none is selected
  useEffect(() => {
    if (isAdminMode && !selectedReviewId) {
      if (pendingLodgments.length > 0) {
        setSelectedReviewId(pendingLodgments[0].id);
      } else if (lodgedLodgments.length > 0) {
        setSelectedReviewId(lodgedLodgments[0].id);
      } else if (finalisedLodgments.length > 0) {
        setSelectedReviewId(finalisedLodgments[0].id);
      }
    }
  }, [pendingLodgments, lodgedLodgments, finalisedLodgments, isAdminMode, selectedReviewId]);

  const selectedDraft = drafts.find(d => d.id === selectedReviewId);

  // Sync inputApplicationId when selection changes
  useEffect(() => {
    if (selectedDraft) {
      setInputApplicationId(selectedDraft.form_data?.applicationId || "");
      setInputCaseNumber(selectedDraft.form_data?.caseNumber || "");
      setInputAmount(selectedDraft.form_data?.amount || "");
    } else {
      setInputApplicationId("");
      setInputCaseNumber("");
      setInputAmount("");
    }
  }, [selectedDraft]);

  // Handle matter prefill
  const handleMatterChange = (matterId) => {
    setSelectedMatterId(matterId);
    if (!matterId || matterId === "none_selected") {
      setSelectedMatterId("");
      return;
    }
    
    const matched = matters.find(m => m.id === matterId);
    if (matched) {
      setCaseName(matched.client_name || "");
      setCaseNumber(matched.file_ref || matched.id || "");
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const uploadCertificateFile = async (file) => {
    if (!file) return;
    setUploadingCertificate(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      setAttachedFile({
        name: file.name,
        size: file.size,
        url: file_url
      });
      toast.success(`Attached certificate: ${file.name}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload certificate file.");
    } finally {
      setUploadingCertificate(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      uploadCertificateFile(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      uploadCertificateFile(files[0]);
    }
  };

  // Upload supporting files
  const uploadSupportingFile = async (file) => {
    if (!file) return;
    setUploadingSupporting(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      const newFile = {
        name: file.name,
        size: file.size,
        url: file_url
      };
      setSupportingFiles(prev => [...prev, newFile]);
      toast.success(`Supporting document uploaded: ${file.name}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload supporting document.");
    } finally {
      setUploadingSupporting(false);
    }
  };

  const removeSupportingFile = (index) => {
    setSupportingFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Submit new certificate lodgment
  const handleLodge = async (e) => {
    e.preventDefault();
    if (!caseName) return toast.error("Please enter the Case Name");
    if (!court) return toast.error("Please select the Court");
    if (!attachedFile) return toast.error("Please upload the signed Appeal Cost Certificate PDF");

    const draftId = selectedDraftId || `lodgment_${Date.now()}`;
    const newLodgment = {
      caseName,
      caseNumber: "", // Filled in by CLS Admin during review
      court,
      certificateDate,
      amount: "", // Filled in by CLS Admin during review
      notes,
      status: "pending",
      fileName: attachedFile.name,
      fileSize: attachedFile.size,
      fileUrl: attachedFile.url || "",
      supportingFiles: supportingFiles,
      lodgedDate: new Date().toISOString().split("T")[0],
      finalisedDate: null
    };

    try {
      await saveDraft({
        id: draftId,
        formType: "appeal_cost_certificate",
        draftName: `Lodgment: ${caseName}`,
        savedBy: user?.full_name || user?.name || `${brandObj.appPrefix} Solicitor`,
        savedByEmail: user?.email || brandObj.defaultEmail,
        brand: brandType,
        formData: newLodgment,
        matter_id: selectedMatterId || null
      });

      toast.success("Appeal cost certificate lodged successfully!");
      handleNewForm();
    } catch (err) {
      toast.error(err.message || "Failed to lodge certificate");
    }
  };

  // Save current form inputs as in-progress draft
  const handleSaveDraft = async (e) => {
    if (e) e.preventDefault();
    if (!caseName) {
      return toast.error("Please enter a Case Name to save a draft");
    }

    const draftId = selectedDraftId || `lodgment_${Date.now()}`;
    const draftName = `Draft: ${caseName}`;
    const newLodgment = {
      caseName,
      caseNumber: "",
      court,
      certificateDate,
      amount: "",
      notes,
      status: "draft",
      fileName: attachedFile ? attachedFile.name : "",
      fileSize: attachedFile ? attachedFile.size : 0,
      fileUrl: attachedFile ? attachedFile.url : "",
      supportingFiles: supportingFiles,
      lodgedDate: null,
      finalisedDate: null
    };

    try {
      const saved = await saveDraft({
        id: draftId,
        formType: "appeal_cost_certificate",
        draftName: draftName,
        savedBy: user?.full_name || user?.name || `${brandObj.appPrefix} Solicitor`,
        savedByEmail: user?.email || brandObj.defaultEmail,
        brand: brandType,
        formData: newLodgment,
        matter_id: selectedMatterId || null
      });

      // The server names a new draft, so the selection follows what it saved rather than
      // the id built above for this page's own bookkeeping.
      setSelectedDraftId(saved?.id || draftId);
      toast.success("Draft saved successfully!");
    } catch (err) {
      toast.error(err.message || "Failed to save draft");
    }
  };

  // Load select draft back into form inputs
  const handleLoadDraft = (draft) => {
    if (!draft || !draft.form_data) return;
    const fd = draft.form_data;
    setSelectedDraftId(draft.id);
    setCaseName(fd.caseName || "");
    setCaseNumber(fd.caseNumber || "");
    setCourt(fd.court || "");
    setCertificateDate(fd.certificateDate || new Date().toISOString().split("T")[0]);
    setAmount(fd.amount !== undefined ? fd.amount.toString() : "");
    setNotes(fd.notes || "");
    setSelectedMatterId(draft.matter_id || "");
    if (fd.fileName) {
      setAttachedFile({ name: fd.fileName, size: fd.fileSize || 0, url: fd.fileUrl || "" });
    } else {
      setAttachedFile(null);
    }
    if (fd.supportingFiles) {
      setSupportingFiles(fd.supportingFiles);
    } else {
      setSupportingFiles([]);
    }
    toast.success("Draft loaded successfully!");
  };

  // Clear current form inputs and reset draft state
  const handleNewForm = () => {
    setSelectedDraftId("");
    setCaseName("");
    setCaseNumber("");
    setCourt("");
    setCertificateDate(new Date().toISOString().split("T")[0]);
    setAmount("");
    setNotes("");
    setAttachedFile(null);
    setSelectedMatterId("");
    setSupportingFiles([]);
  };

  // Helper to mark a pending lodgment as lodged with the board
  const handleLodgmentComplete = async (draft, appId, caseNum, amt) => {
    if (!draft) return;
    if (!appId.trim()) return toast.error("Please enter the Application ID");
    if (!caseNum.trim()) return toast.error("Please enter the Appeals Registry No.");
    if (!amt || isNaN(Number(amt))) return toast.error("Please enter a valid Certified Amount");

    const updatedData = {
      ...draft.form_data,
      status: "lodged",
      applicationId: appId,
      caseNumber: caseNum,
      amount: Number(amt),
      lodgedWithBoardDate: new Date().toISOString().split("T")[0]
    };

    try {
      await saveDraft({
        id: draft.id,
        formType: "appeal_cost_certificate",
        draftName: `Lodgment: ${draft.form_data.caseName} (${caseNum})`,
        savedBy: draft.saved_by,
        savedByEmail: draft.saved_by_email,
        brand: brandType,
        formData: updatedData,
        matter_id: draft.matter_id
      });
      toast.success("Lodgment marked as complete! Awaiting board outcome.");
    } catch (err) {
      toast.error(err.message || "Failed to complete lodgment");
    }
  };

  // Helper to upload the outcome letter
  const handleOutcomeUpload = async (files, draft) => {
    const file = files?.[0];
    if (!file || !draft) return;

    setUploadingOutcome(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      
      const updatedData = {
        ...draft.form_data,
        outcomeLetterUrl: file_url,
        outcomeLetterName: file.name,
        outcomeLetterSize: file.size
      };

      await saveDraft({
        id: draft.id,
        formType: "appeal_cost_certificate",
        draftName: draft.draft_name,
        savedBy: draft.saved_by,
        savedByEmail: draft.saved_by_email,
        brand: brandType,
        formData: updatedData,
        matter_id: draft.matter_id
      });
      
      toast.success(`Outcome letter uploaded: ${file.name}`);
    } catch (err) {
      console.error("Error uploading outcome letter:", err);
      toast.error("Failed to upload outcome letter.");
    } finally {
      setUploadingOutcome(false);
    }
  };

  // Helper to finalise the lodged application
  const handleFinaliseWithBoard = async (draft) => {
    if (!draft) return;
    if (!draft.form_data?.outcomeLetterUrl) {
      return toast.error("Please upload the outcome letter before finalising.");
    }

    const updatedData = {
      ...draft.form_data,
      status: "finalised",
      finalisedDate: new Date().toISOString().split("T")[0]
    };

    try {
      await saveDraft({
        id: draft.id,
        formType: "appeal_cost_certificate",
        draftName: draft.draft_name,
        savedBy: draft.saved_by,
        savedByEmail: draft.saved_by_email,
        brand: brandType,
        formData: updatedData,
        matter_id: draft.matter_id
      });
      toast.success("Application successfully finalised with the board!");
    } catch (err) {
      toast.error(err.message || "Failed to finalise application");
    }
  };

  // Helper to download files programmatically (supports base64 data URLs)
  const handleDownloadFile = (fileUrl, fileName) => {
    if (!fileUrl) {
      toast.error("No file content is stored for this lodgment (only name/size were saved prior to storage integration).");
      return;
    }
    
    try {
      if (fileUrl.startsWith("data:")) {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = fileName || "download.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Downloaded: ${fileName}`);
      } else {
        window.open(fileUrl, '_blank');
      }
    } catch (err) {
      console.error("Failed to download file:", err);
      toast.error("Failed to download file.");
    }
  };

  // Delete/Cancel a lodgment or draft
  const handleDelete = async (draftId) => {
    if (!window.confirm("Are you sure you want to delete this lodgment record?")) return;
    try {
      await deleteDraft(draftId, "appeal_cost_certificate", brandType);
      toast.success("Lodgment record deleted.");
      if (selectedReviewId === draftId) {
        setSelectedReviewId("");
      }
      if (selectedDraftId === draftId) {
        setSelectedDraftId("");
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete record");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* The typed content only. Attachments are File objects and cannot be written to a
            draft, and the drag, upload and preview flags are momentary UI state that would
            be wrong to restore. */}
        <div className="flex justify-end print:hidden">
          <CloudDraftsPanel
            formType="appeal_cost_certificate"
            formData={{ caseName, caseNumber, court, certificateDate, amount, notes, selectedMatterId }}
            onLoadDraft={(draft) => {
              if (!draft) return;
              setCaseName(draft.caseName ?? "");
              setCaseNumber(draft.caseNumber ?? "");
              setCourt(draft.court ?? "");
              if (draft.certificateDate) setCertificateDate(draft.certificateDate);
              setAmount(draft.amount ?? "");
              setNotes(draft.notes ?? "");
              setSelectedMatterId(draft.selectedMatterId ?? "");
            }}
            brand={brandType}
          />
        </div>
        
        {/* Title Header */}
        <div className="flex items-center justify-between border-b border-slate-200 pb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <FileCheck className={`w-7 h-7 ${brandObj.iconColor}`} />
              {isAdminMode ? `${brandObj.appPrefix} Appeal Cost Certificates Console` : "Appeal Cost Certificate Lodgment"}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {isAdminMode 
                ? `Review appeal cost certificates submitted by ${brandObj.appPrefix} lawyers, process lodgments via the Justice Portal, and track recoveries.`
                : `Lodge, track, and manage court appeal cost certificates for ${brandObj.orgName} matters.`}
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => fetchDrafts("appeal_cost_certificate", brandType)}
            className="flex items-center gap-1.5"
            disabled={isLoading}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* LEFT COLUMN: Lodge Form (Solicitor) OR Review Details (Admin) */}
          <div className="lg:col-span-5 space-y-6">
            {isAdminMode && (
              /* ADMIN MODE: Review Lodged Certificate Box */
              selectedDraft ? (
                <Card className={`${brandObj.borderColor} shadow-sm`}>
                  <CardHeader className={`${brandObj.bgHeader} text-white rounded-t-lg py-4`}>
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <FolderOpen className="w-5 h-5" />
                      Review Lodged Certificate
                    </CardTitle>
                    <CardDescription className={`${brandObj.textHeaderColor} text-xs`}>
                      Verify details submitted by the lawyer and lodge on the Justice Portal.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-5 space-y-4">
                    <div className="space-y-4">
                      <div>
                        <Label className="text-[10px] uppercase font-bold text-slate-400">Case Name</Label>
                        <div className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-0.5">{selectedDraft.form_data.caseName}</div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-[10px] uppercase font-bold text-slate-400">Originating Court</Label>
                          <div className="text-xs text-slate-700 dark:text-slate-300 mt-0.5">{selectedDraft.form_data.court}</div>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase font-bold text-slate-400">Certificate Date</Label>
                          <div className="text-xs text-slate-700 dark:text-slate-300 mt-0.5">{selectedDraft.form_data.certificateDate}</div>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase font-bold text-slate-400">Status</Label>
                          <div className="text-xs font-semibold capitalize mt-0.5">
                            {selectedDraft.form_data.status === "pending" && <span className="text-amber-600">Pending</span>}
                            {selectedDraft.form_data.status === "lodged" && <span className="text-indigo-600">Lodged</span>}
                            {selectedDraft.form_data.status === "finalised" && <span className="text-green-600">Finalised</span>}
                          </div>
                        </div>
                      </div>

                      {selectedDraft.form_data.fileName && (
                        <div>
                          <Label className="text-[10px] uppercase font-bold text-slate-400">Certificate PDF</Label>
                          <div className="flex items-center gap-2 mt-1.5 p-2 bg-slate-50 dark:bg-slate-800 border rounded-lg border-slate-100 dark:border-slate-700">
                            <FileText className={`w-5 h-5 ${brandObj.iconColor} shrink-0`} />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">{selectedDraft.form_data.fileName}</div>
                              <div className="text-[10px] text-slate-400">{(selectedDraft.form_data.fileSize / 1024).toFixed(1)} KB</div>
                            </div>
                            <Button 
                              type="button" 
                              variant="outline" 
                              size="xs"
                              onClick={() => handleDownloadFile(selectedDraft.form_data.fileUrl, selectedDraft.form_data.fileName)}
                            >
                              Download
                            </Button>
                          </div>
                        </div>
                      )}

                      {selectedDraft.form_data.supportingFiles && selectedDraft.form_data.supportingFiles.length > 0 && (
                        <div>
                          <Label className="text-[10px] uppercase font-bold text-slate-400">Supporting Documents</Label>
                          <div className="mt-1.5 space-y-1.5">
                            {selectedDraft.form_data.supportingFiles.map((file, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 border rounded-lg border-slate-100 dark:border-slate-700 text-xs">
                                <span className="truncate font-medium">{file.name}</span>
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="xs"
                                  onClick={() => handleDownloadFile(file.url, file.name)}
                                >
                                  Download
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedDraft.form_data.notes && (
                        <div>
                          <Label className="text-[10px] uppercase font-bold text-slate-400">Lawyer Notes</Label>
                          <div className="text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 p-2.5 rounded border border-slate-100 dark:border-slate-700 mt-1 whitespace-pre-wrap">
                            {selectedDraft.form_data.notes}
                          </div>
                        </div>
                      )}

                      {/* WORKFLOW CONTROLS BASED ON STATUS */}
                      {selectedDraft.form_data.status === "pending" && (
                        <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                          <div>
                            <a 
                              href={JUSTICE_PORTAL_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white text-xs font-semibold py-2.5 rounded shadow-sm transition text-center"
                            >
                              <span>Lodge with the Board</span>
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label htmlFor="review-case-num" className="text-xs font-semibold text-slate-700">Appeals Registry No.</Label>
                              <Input 
                                id="review-case-num"
                                value={inputCaseNumber}
                                onChange={e => setInputCaseNumber(e.target.value)}
                                placeholder="e.g. AP-26-0045"
                                className="text-xs"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="review-amount" className="text-xs font-semibold text-slate-700">Certified Amount ($)</Label>
                              <Input 
                                id="review-amount"
                                type="number"
                                step="0.01"
                                value={inputAmount}
                                onChange={e => setInputAmount(e.target.value)}
                                placeholder="0.00"
                                className="text-xs"
                              />
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="review-app-id" className="text-xs font-semibold text-slate-700">Application ID</Label>
                            <Input 
                              id="review-app-id"
                              value={inputApplicationId}
                              onChange={e => setInputApplicationId(e.target.value)}
                              placeholder="Enter board Application ID..."
                              className="text-xs"
                            />
                          </div>

                          <Button 
                            onClick={() => handleLodgmentComplete(selectedDraft, inputApplicationId, inputCaseNumber, inputAmount)}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs py-2 rounded shadow-sm transition"
                          >
                            Lodgment Complete
                          </Button>
                        </div>
                      )}

                      {selectedDraft.form_data.status === "lodged" && (
                        <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-[10px] uppercase font-bold text-slate-400">Appeals Registry No.</Label>
                              <div className="text-sm font-semibold text-slate-800 mt-0.5">{selectedDraft.form_data.caseNumber}</div>
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase font-bold text-slate-400">Certified Amount</Label>
                              <div className="text-sm font-bold text-slate-800 mt-0.5">
                                ${Number(selectedDraft.form_data.amount || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-[10px] uppercase font-bold text-slate-400">Application ID</Label>
                              <div className="text-sm font-semibold text-slate-800 mt-0.5">{selectedDraft.form_data.applicationId}</div>
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase font-bold text-slate-400">Lodged Date</Label>
                              <div className="text-xs text-slate-700 mt-0.5">{selectedDraft.form_data.lodgedWithBoardDate || selectedDraft.form_data.lodgedDate}</div>
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Outcome Letter (PDF)</Label>
                            <div 
                              onDragOver={handleDragOver}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => {
                                e.preventDefault();
                                setIsDragging(false);
                                const files = Array.from(e.dataTransfer.files || []);
                                handleOutcomeUpload(files, selectedDraft);
                              }}
                              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
                                isDragging ? brandObj.bgDragActive : "border-slate-200 hover:border-blue-400"
                              }`}
                              onClick={() => document.getElementById("outcome-file-upload").click()}
                            >
                              <input 
                                type="file" 
                                id="outcome-file-upload" 
                                accept=".pdf"
                                className="hidden" 
                                onChange={(e) => handleOutcomeUpload(Array.from(e.target.files || []), selectedDraft)}
                              />
                              <Upload className="mx-auto h-6 w-6 text-slate-400 mb-1" />
                              {selectedDraft.form_data.outcomeLetterName ? (
                                <div className="flex items-center justify-between gap-3 text-left w-full">
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs font-semibold text-blue-600 truncate">{selectedDraft.form_data.outcomeLetterName}</p>
                                    <p className="text-[10px] text-slate-400">{(selectedDraft.form_data.outcomeLetterSize / 1024).toFixed(1)} KB</p>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="w-7 h-7 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded shrink-0"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (!window.confirm("Are you sure you want to remove the uploaded outcome letter?")) return;
                                      
                                      const updatedData = {
                                        ...selectedDraft.form_data,
                                        outcomeLetterUrl: "",
                                        outcomeLetterName: "",
                                        outcomeLetterSize: 0
                                      };

                                      try {
                                        await saveDraft({
                                          id: selectedDraft.id,
                                          formType: "appeal_cost_certificate",
                                          draftName: selectedDraft.draft_name,
                                          savedBy: selectedDraft.saved_by,
                                          savedByEmail: selectedDraft.saved_by_email,
                                          brand: brandType,
                                          formData: updatedData,
                                          matter_id: selectedDraft.matter_id
                                        });
                                        toast.success("Outcome letter removed.");
                                      } catch (err) {
                                        toast.error("Failed to remove outcome letter.");
                                      }
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  <p className="text-xs font-medium text-slate-600">Upload Outcome Letter</p>
                                  <p className="text-[10px] text-slate-400">PDF only</p>
                                </div>
                              )}
                            </div>
                          </div>

                          <Button 
                            onClick={() => handleFinaliseWithBoard(selectedDraft)}
                            disabled={!selectedDraft.form_data.outcomeLetterUrl || uploadingOutcome}
                            className="w-full bg-green-700 hover:bg-green-800 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white font-semibold text-xs py-2 rounded shadow-sm transition"
                          >
                            {uploadingOutcome ? "Uploading..." : "Application Finalised with the Board"}
                          </Button>
                        </div>
                      )}

                      {selectedDraft.form_data.status === "finalised" && (
                        <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-[10px] uppercase font-bold text-slate-400">Appeals Registry No.</Label>
                              <div className="text-sm font-semibold text-slate-800 mt-0.5">{selectedDraft.form_data.caseNumber}</div>
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase font-bold text-slate-400">Certified Amount</Label>
                              <div className="text-sm font-bold text-slate-800 mt-0.5">
                                ${Number(selectedDraft.form_data.amount || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-[10px] uppercase font-bold text-slate-400">Application ID</Label>
                              <div className="text-sm font-semibold text-slate-800 mt-0.5">{selectedDraft.form_data.applicationId}</div>
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase font-bold text-slate-400">Finalised Date</Label>
                              <div className="text-xs text-slate-700 mt-0.5">{selectedDraft.form_data.finalisedDate}</div>
                            </div>
                          </div>
                          {selectedDraft.form_data.outcomeLetterUrl && (
                            <div>
                              <Label className="text-[10px] uppercase font-bold text-slate-400">Outcome Letter</Label>
                              <div className="flex items-center gap-2 mt-1.5 p-2 bg-green-50 dark:bg-green-950/20 border rounded-lg border-green-100">
                                <FileCheck className="w-5 h-5 text-green-700 shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-semibold text-green-800 truncate">{selectedDraft.form_data.outcomeLetterName}</div>
                                  <div className="text-[10px] text-green-600">{(selectedDraft.form_data.outcomeLetterSize / 1024).toFixed(1)} KB</div>
                                </div>
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="xs"
                                  onClick={() => handleDownloadFile(selectedDraft.form_data.outcomeLetterUrl, selectedDraft.form_data.outcomeLetterName)}
                                >
                                  Download
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 shadow-none border-dashed p-6 text-center flex flex-col items-center justify-center min-h-[300px]">
                  <FileText className="w-10 h-10 text-slate-400 mb-3 animate-pulse" />
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-350">No Lodgment Selected</h3>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1.5 max-w-[280px] mx-auto leading-relaxed">
                    Select a certificate from the Pending, Lodged, or Finalised lists on the right to review details and take action.
                  </p>
                </Card>
              )
            )}

            {/* The lodging form. A firm lodges with it, and so does an administrator
                working on that firm's behalf - the certificate is filed under this tool's
                firm either way, so nothing can land against the wrong one. */}
            <Card className={`${brandObj.borderColor} shadow-sm`}>
                <CardHeader className={`${brandObj.bgHeader} text-white rounded-t-lg py-4`}>
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    Lodge New Certificate
                  </CardTitle>
                  <CardDescription className={`${brandObj.textHeaderColor} text-xs`}>
                    {isAdminMode
                      ? `Lodging on behalf of ${brandObj.appPrefix}. It joins the pending list below for review.`
                      : "Fill in case details and attach the signed appeal cost certificate."}
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-5 space-y-4">
                  <form onSubmit={handleLodge} className="space-y-4">
                    
                    {/* Associate with Matter */}
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">Associate with Matter (Optional)</Label>
                      <Select value={selectedMatterId} onValueChange={handleMatterChange}>
                        <SelectTrigger className="mt-1.5 text-xs">
                          <SelectValue placeholder="Select active client matter..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none_selected">None (Manual Entry)</SelectItem>
                          {matters.map(m => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.client_name} ({m.file_ref || m.id})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Case Name */}
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">Case Name</Label>
                      <Input 
                        value={caseName} 
                        onChange={e => setCaseName(e.target.value)} 
                        placeholder="e.g. R v Smith"
                        className="mt-1.5 text-xs"
                        required
                      />
                    </div>

                    {/* Date and Court side-by-side */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-semibold text-slate-700">Date of Certificate</Label>
                        <Input 
                          type="date"
                          value={certificateDate} 
                          onChange={e => setCertificateDate(e.target.value)} 
                          className="mt-1.5 text-xs"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-slate-700">Originating Court</Label>
                        <Select value={court} onValueChange={setCourt}>
                          <SelectTrigger className="mt-1.5 text-xs">
                            <SelectValue placeholder="Select court..." />
                          </SelectTrigger>
                          <SelectContent>
                            {COURTS.map(c => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* PDF Upload Dropzone */}
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">Certificate Attachment (PDF)</Label>
                      <div 
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className={`mt-1.5 border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-all ${
                          isDragging ? brandObj.bgDragActive : brandObj.borderDragInactive
                        }`}
                        onClick={() => document.getElementById("certificate-file-upload").click()}
                      >
                        <input 
                          type="file" 
                          id="certificate-file-upload" 
                          accept=".pdf"
                          className="hidden" 
                          onChange={handleFileSelect}
                        />
                        <Upload className="mx-auto h-8 w-8 text-slate-400 mb-2" />
                        {attachedFile ? (
                          <div className="flex items-center justify-between gap-3 text-left w-full">
                            <div className="min-w-0 flex-1">
                              <p className={`text-xs font-semibold ${brandObj.textColor} truncate`}>{attachedFile.name}</p>
                              <p className="text-[10px] text-slate-400">{(attachedFile.size / 1024).toFixed(1)} KB</p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="w-7 h-7 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAttachedFile(null);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-slate-600">Drag & Drop Signed Certificate here</p>
                            <p className="text-[10px] text-slate-400">or click to browse local files (PDF only)</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Supporting Documents (Optional) */}
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">Supporting Documents (Optional)</Label>
                      <div 
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => {
                          e.preventDefault();
                          setIsDragging(false);
                          const files = Array.from(e.dataTransfer.files || []);
                          if (files.length > 0) {
                            uploadSupportingFile(files[0]);
                          }
                        }}
                        className="mt-1.5 border border-dashed rounded-lg p-3 text-center cursor-pointer hover:border-blue-400 transition-all border-slate-200 hover:border-blue-450"
                        onClick={() => document.getElementById("supporting-file-upload").click()}
                      >
                        <input 
                          type="file" 
                          id="supporting-file-upload" 
                          className="hidden" 
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            if (files.length > 0) {
                              uploadSupportingFile(files[0]);
                            }
                          }}
                        />
                        <Plus className="mx-auto h-5 w-5 text-slate-400 mb-1" />
                        <span className="text-xs font-semibold text-slate-600 block">Click to upload supporting documents</span>
                      </div>
                      
                      {/* List of supporting documents */}
                      {supportingFiles.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {supportingFiles.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between p-1.5 bg-slate-50 border rounded text-xs">
                              <span className="truncate max-w-[200px] font-medium">{file.name}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="w-5 h-5 text-rose-500 hover:text-rose-700"
                                onClick={() => removeSupportingFile(idx)}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Notes */}
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">Lodgment Notes</Label>
                      <Textarea 
                        value={notes} 
                        onChange={e => setNotes(e.target.value)} 
                        placeholder="Add any specific comments or reference notes..."
                        className="mt-1.5 text-xs"
                        rows={2}
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Button 
                          type="button" 
                          onClick={handleSaveDraft}
                          variant="outline"
                          className="flex-1 text-xs font-semibold py-2 rounded border-slate-250 hover:bg-slate-50 transition"
                          disabled={isLoading}
                        >
                          Save as Draft
                        </Button>
                        <Button 
                          type="submit" 
                          className={`flex-[2] ${brandObj.bgButton} text-white font-semibold text-xs py-2 rounded shadow-sm transition`}
                          disabled={isLoading}
                        >
                          Submit Certificate Lodgment
                        </Button>
                      {/* An appeal cost certificate is funding paperwork: it belongs with
                          the VLA documents on the matter. */}
                      <ActionstepSavePanel
                        formLabel="Appeal Cost Certificate"
                        disabled={!caseName}
                        saveNote={false}
                        getDocuments={async () => {
                          const doc = createPortalPdf({
                            title: "Appeal Cost Certificate",
                            brandColor: "#0F766E",
                          });
                          doc.heading("Matter");
                          doc.fields([
                            ["Case name", caseName],
                            ["Case number", caseNumber],
                            ["Court", court],
                            ["Certificate date", certificateDate],
                            ["Amount", amount],
                          ]);
                          if (notes) {
                            doc.heading("Notes");
                            doc.body(notes);
                          }
                          return [{
                            file: doc.toFile(portalDocumentName("Appeal-Cost-Certificate", caseName, certificateDate)),
                            folder: actionstepFolderFor("aidApplication"),
                          }];
                        }}
                      />
                      </div>
                      {selectedDraftId && (
                        <Button 
                          type="button" 
                          onClick={handleNewForm}
                          variant="ghost"
                          className="w-full text-[10px] text-slate-500 hover:text-slate-700 font-semibold py-1 transition"
                        >
                          Clear Form (Start New Lodgment)
                        </Button>
                      )}
                    </div>
                  </form>
                </CardContent>
              </Card>
          </div>

          {/* RIGHT COLUMN: Pending (Box 2) & Finalised (Box 3) */}
          <div className="lg:col-span-7 space-y-6">
            
            {/* BOX 1.5: Saved Drafts (Solicitor Mode Only) */}
            {!isAdminMode && (
              <Card className="border-purple-100 shadow-sm">
                <CardHeader className="bg-gradient-to-r from-purple-700 to-purple-650 text-white rounded-t-lg py-4">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Saved Drafts
                  </CardTitle>
                  <CardDescription className="text-purple-100 text-xs">
                    In-progress lodgment drafts. Click to load one back into the form.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  {savedDrafts.length === 0 ? (
                    <div className="p-8 text-center text-slate-400 text-xs font-medium">
                      No saved drafts found.
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-[250px] overflow-y-auto">
                      {savedDrafts.map((d) => {
                        const isSelected = selectedDraftId === d.id;
                        return (
                          <div 
                            key={d.id} 
                            onClick={() => handleLoadDraft(d)}
                            className={`p-4 hover:bg-slate-50 transition cursor-pointer flex items-center justify-between gap-4 ${
                              isSelected ? "bg-purple-50/50 border-l-4 border-purple-600" : ""
                            }`}
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-semibold text-slate-800 truncate">{d.draft_name || "Unnamed Case"}</p>
                              <p className="text-[10px] text-slate-500 mt-1">
                                Saved: {new Date(d.updated_at).toLocaleString()} | Matter: {d.matter_id || "None"}
                              </p>
                            </div>
                            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="w-7 h-7 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg"
                                onClick={() => handleDelete(d.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* BOX 2: Pending Lodgments */}
            <Card className="border-amber-100 shadow-sm">
              <CardHeader className="bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-t-lg py-4">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Pending Lodgments
                </CardTitle>
                <CardDescription className="text-amber-100 text-xs">
                  {isAdminMode 
                    ? `Certificates submitted by ${brandObj.appPrefix} lawyers. Click an item to load details into the review panel.`
                    : "Certificates awaiting registry confirmation or payment processing."}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {pendingLodgments.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-xs font-medium">
                    No pending cost certificate lodgments.
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-[350px] overflow-y-auto">
                    {pendingLodgments.map(draft => {
                      const data = draft.form_data || {};
                      const isSelected = selectedReviewId === draft.id;
                      return (
                        <div 
                          key={draft.id} 
                          className={`p-4 hover:bg-slate-50/50 dark:hover:bg-slate-850/50 transition-all flex items-center justify-between gap-4 cursor-pointer ${
                            isAdminMode && isSelected ? brandObj.bgReviewActive : ""
                          }`}
                          onClick={() => {
                            if (isAdminMode) {
                              setSelectedReviewId(draft.id);
                            }
                          }}
                        >
                          <div className="space-y-1.5 flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate">{data.caseName}</span>
                              <span className="text-[10px] bg-amber-50 text-amber-700 font-semibold px-2 py-0.5 rounded-full border border-amber-200">
                                Pending
                              </span>
                            </div>
                            <div className="flex items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 flex-wrap">
                              <span className="flex items-center gap-1 font-medium">
                                <FileText className="w-3 h-3" />
                                {data.caseNumber}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Submitted: {data.lodgedDate}
                              </span>
                              <span className={`font-semibold ${brandObj.textMutedColor} px-1.5 py-0.2 rounded`}>
                                ${Number(data.amount || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                            {data.fileName && (
                              <div className="text-[10px] text-slate-400 flex items-center gap-1 italic">
                                <FileText className={`w-2.5 h-2.5 ${brandObj.iconSmallColor}`} />
                                Attachment: {data.fileName}
                              </div>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                            <Button 
                              onClick={() => handleDelete(draft.id)}
                              variant="ghost" 
                              size="icon"
                              className="text-slate-400 hover:text-red-600 h-7 w-7"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* BOX 2.5: Lodged with the Board - Awaiting Outcome */}
            <Card className="border-indigo-100 shadow-sm">
              <CardHeader className="bg-gradient-to-r from-indigo-700 to-indigo-650 text-white rounded-t-lg py-4">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Lodged with the Board - Awaiting Outcome
                </CardTitle>
                <CardDescription className="text-indigo-100 text-xs">
                  {isAdminMode 
                    ? "Applications lodged with the board awaiting outcome decisions."
                    : "Applications currently being processed by the board."}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {lodgedLodgments.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-xs font-medium">
                    No applications currently awaiting board outcome.
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-[350px] overflow-y-auto">
                    {lodgedLodgments.map(draft => {
                      const data = draft.form_data || {};
                      const isSelected = selectedReviewId === draft.id;
                      return (
                        <div 
                          key={draft.id} 
                          className={`p-4 hover:bg-slate-50/50 dark:hover:bg-slate-850/50 transition-all flex items-center justify-between gap-4 cursor-pointer ${
                            isAdminMode && isSelected ? brandObj.bgReviewActive : ""
                          }`}
                          onClick={() => {
                            if (isAdminMode) {
                              setSelectedReviewId(draft.id);
                            }
                          }}
                        >
                          <div className="space-y-1.5 flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate">{data.caseName}</span>
                              <span className="text-[10px] bg-indigo-50 text-indigo-700 font-semibold px-2 py-0.5 rounded-full border border-indigo-200">
                                Awaiting Outcome
                              </span>
                            </div>
                            <div className="flex items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 flex-wrap">
                              <span className="flex items-center gap-1 font-medium">
                                <FileText className="w-3 h-3" />
                                App ID: {data.applicationId}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Lodged: {data.lodgedWithBoardDate || data.lodgedDate}
                              </span>
                              <span className={`font-semibold ${brandObj.textMutedColor} px-1.5 py-0.2 rounded`}>
                                ${Number(data.amount || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                            {data.outcomeLetterName && (
                              <div className="text-[10px] text-green-600 flex items-center gap-1 italic">
                                <FileCheck className="w-2.5 h-2.5 text-green-600" />
                                Outcome: {data.outcomeLetterName}
                              </div>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                            <Button 
                              onClick={() => handleDelete(draft.id)}
                              variant="ghost" 
                              size="icon"
                              className="text-slate-400 hover:text-red-600 h-7 w-7"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* BOX 3: Finalised Lodgments */}
            <Card className="border-green-100 shadow-sm">
              <CardHeader className="bg-gradient-to-r from-green-800 to-green-700 text-white rounded-t-lg py-4">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  Application Finalised with the Board
                </CardTitle>
                <CardDescription className="text-green-100 text-xs">
                  Certificates successfully processed and finalised.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {finalisedLodgments.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-xs font-medium">
                    No finalised cost certificate lodgments.
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-[350px] overflow-y-auto">
                    {finalisedLodgments.map(draft => {
                      const data = draft.form_data || {};
                      const isSelected = selectedReviewId === draft.id;
                      return (
                        <div 
                          key={draft.id} 
                          className={`p-4 hover:bg-slate-50/50 dark:hover:bg-slate-850/50 transition-colors flex items-center justify-between gap-4 cursor-pointer ${
                            isAdminMode && isSelected ? brandObj.bgReviewActive : ""
                          }`}
                          onClick={() => {
                            if (isAdminMode) {
                              setSelectedReviewId(draft.id);
                            }
                          }}
                        >
                          <div className="space-y-1 flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate">{data.caseName}</span>
                              <span className="text-[10px] bg-green-50 text-green-700 font-semibold px-2 py-0.5 rounded-full border border-green-200 flex items-center gap-0.5">
                                <CheckCircle2 className="w-2.5 h-2.5" />
                                Finalised
                              </span>
                            </div>
                            <div className="flex items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 flex-wrap">
                              <span className="flex items-center gap-1 font-medium">
                                <FileText className="w-3 h-3" />
                                App ID: {data.applicationId}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Finalised: {data.finalisedDate}
                              </span>
                              <span className="font-semibold text-green-800 dark:text-green-400 bg-green-50 px-1.5 py-0.2 rounded">
                                ${Number(data.amount || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              onClick={() => handleDelete(draft.id)}
                              variant="ghost" 
                              size="icon"
                              className="text-slate-400 hover:text-red-600 h-7 w-7"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

          </div>
        </div>

      </div>
    </div>
  );
}
